import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { applyTheme } from "@/contexts/ThemeLoader";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import RestockHistoryDrawer from "@/components/RestockHistoryDrawer";
import AdminAddProduct from "./AdminAddProduct";
import AdminBlogManager from "./AdminBlogManager";
import AdminProductEdit from "./AdminProductEdit";
import MessageThread from "@/components/MessageThread";

const Stat = ({ label, value, accent }) => (
  <div className={`brut-border p-5 ${accent ? "bg-[#C77E3C] text-white" : "bg-white"}`}>
    <div className="mono text-[10px] uppercase tracking-[0.3em] opacity-70">{label}</div>
    <div className="heading text-3xl font-black mt-2">{value}</div>
  </div>
);

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("pending");
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [productCategoryFilter, setProductCategoryFilter] = useState("all");
  const [editingProductId, setEditingProductId] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [theme, setTheme] = useState({ bg: "#EDE5D2", ink: "#1F3D2A", accent: "#C77E3C", accent_deep: "#A05F26" });
  const [balances, setBalances] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [featForm, setFeatForm] = useState({ name: "", subtitle: "", hero_image: "", active: true, sort_order: 0 });
  const [editingFeat, setEditingFeat] = useState(null);
  const [featUploading, setFeatUploading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [reminderLog, setReminderLog] = useState([]);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [editingSeller, setEditingSeller] = useState(null); // seller_id currently being edited
  const [sellerDraft, setSellerDraft] = useState({});
  const [conversations, setConversations] = useState([]);
  const [openThread, setOpenThread] = useState(null);
  const [unpaidOnly, setUnpaidOnly] = useState(false);

  useEffect(() => {
    if (!loading) {
      if (!user) nav("/login");
      else if (user.role !== "admin") nav("/");
    }
  }, [loading, user, nav]);

  const refresh = async () => {
    const [s, p, a, sel, o, b, ph, feat, tk, rl, cv] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/products", { params: { status: "pending" } }),
      api.get("/admin/products"),
      api.get("/admin/sellers"),
      api.get("/admin/orders"),
      api.get("/admin/payouts/balances"),
      api.get("/admin/payouts/history"),
      api.get("/admin/featured-artisans"),
      api.get("/admin/tickets"),
      api.get("/admin/reminders/log"),
      api.get("/admin/conversations").catch(() => ({ data: [] })),
    ]);
    setStats(s.data); setPending(p.data); setAllProducts(a.data);
    setSellers(sel.data); setOrders(o.data);
    setBalances(b.data); setPayoutHistory(ph.data);
    setFeatured(feat.data);
    setTickets(tk.data);
    setReminderLog(rl.data);
    setConversations(cv.data);
    try {
      const { data: t } = await api.get("/settings/theme");
      setTheme(t);
    } catch { /* keep default theme */ }
  };

  const runReminders = async () => {
    try {
      const { data } = await api.post("/admin/reminders/run");
      toast.success(`Sent: ${data.delivery} delivery, ${data.event} event, ${data.payout} payout`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const testPayoutEmail = async () => {
    if (!confirm("Send the payout-due digest to admin email right now (ignores last-Monday rule)?")) return;
    try {
      const { data } = await api.post("/admin/reminders/payout-test");
      toast.success(`Sent — ${data.sellers_owed} seller(s) owed $${data.total_owed.toFixed(2)}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openAdminTicket = async (tid) => {
    const token = localStorage.getItem("token");
    const backend = process.env.REACT_APP_BACKEND_URL;
    const r = await fetch(`${backend}/api/tickets/${tid}/download`, { headers: { Authorization: `Bearer ${token}` } });
    const html = await r.text();
    const w = window.open("", "_blank");
    w.document.write(html); w.document.close();
  };

  const saveFeatured = async () => {
    if (!featForm.name || !featForm.hero_image) {
      toast.error("Name and image are required");
      return;
    }
    try {
      if (editingFeat) {
        await api.put(`/admin/featured-artisans/${editingFeat}`, featForm);
        toast.success("Featured artisan updated");
      } else {
        await api.post("/admin/featured-artisans", featForm);
        toast.success("Featured artisan added");
      }
      setFeatForm({ name: "", subtitle: "", hero_image: "", active: true, sort_order: 0 });
      setEditingFeat(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const editFeatured = (f) => {
    setEditingFeat(f.featured_id);
    setFeatForm({ name: f.name, subtitle: f.subtitle || "", hero_image: f.hero_image, active: f.active, sort_order: f.sort_order || 0 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteFeatured = async (fid) => {
    if (!confirm("Delete this featured artisan?")) return;
    await api.delete(`/admin/featured-artisans/${fid}`);
    toast.success("Deleted");
    refresh();
  };

  const uploadFeaturedImage = async (file) => {
    if (!file) return;
    setFeatUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backend = process.env.REACT_APP_BACKEND_URL;
      const fullUrl = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
      setFeatForm((f) => ({ ...f, hero_image: fullUrl }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setFeatUploading(false);
    }
  };

  const markPaid = async (row) => {
    const amount = parseFloat(prompt(`Mark payout for ${row.seller_name}. Amount owed: $${row.owed.toFixed(2)}. Enter amount you're paying:`, row.owed.toFixed(2)));
    if (!amount || amount <= 0) return;
    const reference = prompt("Reference (PayPal txn id / bank ref / mobile money code) — optional:", "");
    if (reference === null) return;
    const note = prompt("Internal note — optional:", "");
    if (note === null) return;
    try {
      await api.post("/admin/payouts/mark-paid", {
        seller_id: row.seller_id,
        amount,
        reference: reference || null,
        note: note || null,
      });
      toast.success(`Recorded $${amount.toFixed(2)} payout to ${row.seller_name}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payout");
    }
  };

  const markAllPaid = async () => {
    const owedRows = balances.filter((b) => b.owed > 0.005);
    if (owedRows.length === 0) { toast.info("Nothing owed — you're all settled"); return; }
    const total = owedRows.reduce((s, r) => s + r.owed, 0);
    if (!confirm(`Auto-mark all ${owedRows.length} seller(s) as paid — $${total.toFixed(2)} total?\n\nThis records a ledger entry per seller. You still need to send the actual money via each seller's method.`)) return;
    const note = prompt("Optional note (applied to all entries):", "");
    if (note === null) return;
    try {
      const { data } = await api.post("/admin/payouts/mark-all-paid", { note: note || null });
      toast.success(`Marked ${data.created} seller(s) paid — $${data.total.toFixed(2)} total`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const exportCsv = () => {
    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("token");
    const qs = unpaidOnly ? "?unpaid_only=true" : "";
    fetch(`${API_BASE}/api/admin/payouts/export.csv${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `jubasmash-payouts${unpaidOnly ? "-unpaid" : ""}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("CSV export failed"));
  };

  const saveTheme = async () => {
    try {
      await api.put("/settings/theme", theme);
      applyTheme(theme);
      toast.success("Theme saved — applied site-wide");
    } catch (e) { toast.error("Failed"); }
  };

  const resetTheme = async () => {
    const { data } = await api.post("/settings/theme/reset");
    setTheme(data);
    applyTheme(data);
    toast.success("Theme reset to defaults");
  };

  const previewTheme = (next) => {
    setTheme(next);
    applyTheme(next);
  };

  useEffect(() => {
    if (user?.role === "admin") refresh();
  }, [user]);

  const setStatus = async (pid, status) => {
    try {
      await api.patch(`/products/${pid}`, { status });
      toast.success(`Marked ${status}`);
      refresh();
    } catch { toast.error("Failed"); }
  };

  const toggleBlock = async (s) => {
    const currently = s.blocked;
    const label = currently ? "unblock" : "block";
    if (!confirm(`Are you sure you want to ${label} ${s.shop_name || s.name}? ${currently ? "Their products will be visible again." : "Their products will be hidden from the catalogue and they can't log in."}`)) return;
    try {
      await api.post(`/admin/sellers/${s.user_id}/${currently ? "unblock" : "block"}`);
      toast.success(`Seller ${label}ed`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeProd = async (pid) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/products/${pid}`);
    refresh();
  };

  const reassignProduct = async (p) => {
    const options = sellers.map((s, i) => `${i + 1}. ${s.shop_name || s.name} (${s.email})`).join("\n");
    const pick = prompt(`Reassign "${p.title}" to which artisan?\n\n${options}\n\nEnter the number:`);
    if (!pick) return;
    const idx = parseInt(pick) - 1;
    if (isNaN(idx) || idx < 0 || idx >= sellers.length) { toast.error("Invalid choice"); return; }
    const chosen = sellers[idx];
    try {
      await api.patch(`/products/${p.product_id}`, { seller_id: chosen.user_id });
      toast.success(`Reassigned to ${chosen.shop_name || chosen.name}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startSellerEdit = (s) => {
    setEditingSeller(s.user_id);
    setSellerDraft({
      name: s.name || "",
      shop_name: s.shop_name || "",
      payout: s.payout || { method: "paypal", paypal_email: "", remit_phone: "", remit_country: "", bank_details: "" },
    });
  };

  const cancelSellerEdit = () => { setEditingSeller(null); setSellerDraft({}); };

  const saveSellerEdit = async () => {
    try {
      const payout = { ...sellerDraft.payout };
      const hasPayout = payout.paypal_email || payout.remit_phone || payout.bank_details;
      await api.patch(`/admin/sellers/${editingSeller}`, {
        name: sellerDraft.name.trim() || null,
        shop_name: sellerDraft.shop_name.trim() || null,
        payout: hasPayout ? payout : null,
      });
      toast.success("Artisan updated");
      cancelSellerEdit();
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const restockProduct = async (p) => {
    if (p.category === "apparel") {
      const vs = p.variant_stock || {};
      const keys = Object.keys(vs);
      if (!keys.length) { toast.error("No variants to restock"); return; }
      const variants = {};
      for (const k of keys) {
        const add = parseInt(prompt(`Add stock to ${k.replace("|"," / ")} (current: ${vs[k]}):`, "0"));
        if (isNaN(add) || add < 0) return;
        if (add > 0) variants[k] = add;
      }
      if (!Object.keys(variants).length) return;
      await api.post(`/products/${p.product_id}/restock`, { variants });
      toast.success(`Restocked ${p.title}`);
    } else {
      const add = parseInt(prompt(`Add units to "${p.title}" (current: ${p.stock ?? "unlimited"}):`, "10"));
      if (isNaN(add) || add <= 0) return;
      await api.post(`/products/${p.product_id}/restock`, { amount: add });
      toast.success(`Added ${add} to ${p.title}`);
    }
    refresh();
  };

  const refund = async (order_id) => {
    const note = prompt("Optional note for the refund (visible internally):", "");
    if (note === null) return;
    try {
      const { data } = await api.post(`/admin/orders/${order_id}/refund`, { note });
      if (data.stripe_refund_id) {
        toast.success(`Refunded via Stripe (${data.stripe_refund_id})`);
      } else if (data.stripe_refund_error) {
        toast.warning("Marked refunded in Jubasmash — but Stripe refund failed. Issue it manually from your Stripe Dashboard.");
      } else {
        toast.success("Order marked refunded");
      }
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refund failed");
    }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <Layout>
      <section className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="mono text-xs uppercase tracking-[0.3em] text-[#C77E3C]">/ Back office</div>
        <h1 className="heading text-5xl font-black uppercase tracking-tight mt-2">Admin control room</h1>

        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Total revenue" value={`$${stats.revenue.toFixed(2)}`} accent />
            <Stat label="30% commission earned" value={`$${stats.commission_earned.toFixed(2)}`} />
            <Stat label="Sellers" value={stats.sellers} />
            <Stat label="Pending products" value={stats.pending_products} />
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-0 brut-border w-fit">
          {[["pending", `Pending (${pending.length})`], ["add", "+ Add product"], ["products", "All products"], ["sellers", "Sellers"], ["orders", "Orders"], ["payouts", `Payouts${balances.filter((b) => b.owed > 0).length ? ` (${balances.filter((b) => b.owed > 0).length})` : ""}`], ["tickets", `Tickets (${tickets.length})`], ["messages", "Messages"], ["reminders", `Reminders (${reminderLog.length})`], ["blog", "Blog"], ["featured", `Featured (${featured.length})`], ["theme", "Theme"]].map(([k, l], i) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`px-5 py-3 mono uppercase text-xs tracking-[0.25em] ${i > 0 ? "border-l-2 border-[#1F3D2A]" : ""} ${tab === k ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>{l}</button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "add" && (
            <AdminAddProduct sellers={sellers} onDone={() => { refresh(); setTab("products"); }} />
          )}

          {tab === "blog" && <AdminBlogManager />}

          {tab === "messages" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admin-messages">
              <div className="brut-border bg-white">
                <div className="bg-[#1F3D2A] text-white px-4 py-3 mono text-[10px] uppercase tracking-[0.3em]">
                  All buyer ⇄ seller conversations
                </div>
                {conversations.length === 0 ? (
                  <div className="p-8 text-center text-[#555] mono uppercase text-xs tracking-[0.2em]">No conversations yet</div>
                ) : conversations.map((c, i) => (
                  <button key={c.conversation_id} data-testid={`admin-conv-${c.conversation_id}`} onClick={() => setOpenThread(c.conversation_id)}
                    className={`w-full text-left p-4 hover:bg-[#FDF3E9] ${i > 0 ? "border-t-2 border-[#1F3D2A]" : ""} ${openThread === c.conversation_id ? "bg-[#FDF3E9]" : ""}`}>
                    <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#C77E3C]">Order {c.order_id.slice(-10)}</div>
                    <div className="text-sm mt-1"><strong>{c.buyer_email}</strong> ⇄ {c.seller_name || c.seller_email}</div>
                    <div className="text-xs text-[#555] mt-1 truncate">{c.last_message_preview || "No messages yet"}</div>
                  </button>
                ))}
              </div>
              <div>
                {openThread ? (
                  <MessageThread conversationId={openThread} readOnly onClose={() => setOpenThread(null)} />
                ) : (
                  <div className="brut-border bg-white p-12 text-center mono text-xs uppercase tracking-[0.25em] text-[#555]">Pick a conversation to read the thread →</div>
                )}
              </div>
            </div>
          )}

          {tab === "pending" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {pending.length === 0 ? (
                <div className="brut-border bg-white p-12 col-span-full text-center mono text-xs uppercase tracking-[0.2em] text-[#555]">No pending submissions</div>
              ) : pending.map((p) => (
                <div key={p.product_id} data-testid={`pending-${p.product_id}`} className="brut-border bg-white flex gap-4 p-4">
                  <div className="w-32 h-32 brut-border bg-[#EAEAEA] overflow-hidden flex-shrink-0">
                    {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <div className="mono text-xs uppercase tracking-[0.2em] text-[#555]">{p.type} · {p.category} · by {p.seller_name}</div>
                    <div className="heading text-lg font-bold mt-1">{p.title}</div>
                    <div className="text-sm text-[#333] mt-1 line-clamp-3">{p.description}</div>
                    <div className="mono font-bold mt-2">${p.price.toFixed(2)}</div>
                    <div className="mt-3 flex gap-2">
                      <button data-testid={`approve-${p.product_id}`} onClick={() => setStatus(p.product_id, "approved")} className="brut-border bg-[#C77E3C] text-white px-3 py-2 mono uppercase text-xs tracking-[0.2em] hover:bg-[#1F3D2A]">Approve</button>
                      <button data-testid={`reject-${p.product_id}`} onClick={() => setStatus(p.product_id, "rejected")} className="brut-border bg-white px-3 py-2 mono uppercase text-xs tracking-[0.2em] hover:bg-[#1F3D2A] hover:text-white">Reject</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "products" && (
            <div className="brut-border bg-white overflow-x-auto">
              <div className="p-3 border-b-2 border-[#1F3D2A] bg-[#FDF3E9] flex items-center gap-3 flex-wrap">
                <label className="mono text-[10px] uppercase tracking-[0.25em] text-[#1F3D2A]">Filter by category:</label>
                <select
                  data-testid="admin-products-category-filter"
                  value={productCategoryFilter}
                  onChange={(e) => setProductCategoryFilter(e.target.value)}
                  className="brut-border bg-white px-3 py-2 mono text-xs uppercase tracking-[0.2em]"
                >
                  <option value="all">All ({allProducts.length})</option>
                  <option value="book">Books</option>
                  <option value="music">Music</option>
                  <option value="art">Objects / Art</option>
                  <option value="apparel">Apparel</option>
                  <option value="ticket">Tickets / Events</option>
                  <option value="stream">Streams</option>
                  <option value="other">Other</option>
                </select>
                {productCategoryFilter !== "all" && (
                  <button
                    data-testid="admin-products-clear-filter"
                    onClick={() => setProductCategoryFilter("all")}
                    className="brut-border bg-white px-3 py-2 mono text-[10px] uppercase tracking-[0.25em] hover:bg-[#C77E3C] hover:text-white"
                  >Clear</button>
                )}
                <div className="ml-auto mono text-[10px] uppercase tracking-[0.25em] text-[#555]" data-testid="admin-products-count">
                  {(productCategoryFilter === "all" ? allProducts.length : allProducts.filter((p) => p.category === productCategoryFilter).length)} shown
                </div>
              </div>
              <table className="w-full">
                <thead className="bg-[#1F3D2A] text-white">
                  <tr className="mono text-[10px] uppercase tracking-[0.25em]">
                    <th className="text-left p-3">Title</th><th className="text-left p-3">Seller</th>
                    <th className="text-left p-3">Type</th><th className="text-right p-3">Price</th>
                    <th className="text-left p-3">Status</th><th className="text-right p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(productCategoryFilter === "all" ? allProducts : allProducts.filter((p) => p.category === productCategoryFilter)).map((p) => (
                    <tr key={p.product_id} className="border-t-2 border-[#1F3D2A]">
                      <td className="p-3 text-sm font-bold">
                        {p.title}
                        {p.category === "ticket" && p.stock != null && (
                          <div className="mono text-[10px] uppercase tracking-[0.2em] mt-1" style={{ color: p.sold_out ? "#C77E3C" : "#555" }}>
                            {p.tickets_sold || 0} / {p.stock} tickets sold{p.sold_out && " · SOLD OUT"}
                          </div>
                        )}
                        {p.category === "apparel" && p.variant_total_remaining != null && (
                          <div className="mono text-[10px] uppercase tracking-[0.2em] mt-1" style={{ color: p.sold_out ? "#C77E3C" : "#555" }}>
                            {p.variant_total_remaining} across variants{p.sold_out && " · SOLD OUT"}
                          </div>
                        )}
                        {p.category !== "ticket" && p.category !== "apparel" && (
                          <div className="mono text-[10px] uppercase tracking-[0.2em] mt-1" style={{ color: p.sold_out ? "#C77E3C" : "#555" }}>
                            {p.stock == null ? "Unlimited" : `${p.stock} in stock${p.sold_out ? " · SOLD OUT" : ""}`}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-sm">{p.seller_name}</td>
                      <td className="p-3 text-sm mono uppercase">{p.category === "ticket" ? "ticket" : p.type}</td>
                      <td className="p-3 text-sm mono text-right">${p.price.toFixed(2)}</td>
                      <td className="p-3"><span className={`pill-label ${p.status === "approved" ? "bg-[#C77E3C]" : "bg-[#1F3D2A]"}`}>{p.status}</span></td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <button data-testid={`admin-edit-${p.product_id}`} onClick={() => setEditingProductId(p.product_id)} className="mono text-xs uppercase tracking-[0.2em] mr-3 hover:text-[#C77E3C]">Edit</button>
                        {p.status !== "approved" && <button onClick={() => setStatus(p.product_id, "approved")} className="mono text-xs uppercase tracking-[0.2em] mr-3 hover:text-[#C77E3C]">Approve</button>}
                        {p.status === "approved" && <button data-testid={`admin-restock-${p.product_id}`} onClick={() => restockProduct(p)} className="mono text-xs uppercase tracking-[0.2em] mr-3 hover:text-[#C77E3C]">Restock +</button>}
                        {p.status === "approved" && <button data-testid={`admin-history-${p.product_id}`} onClick={() => setHistoryProduct(p)} className="mono text-xs uppercase tracking-[0.2em] mr-3 hover:text-[#C77E3C]">History</button>}
                        <button data-testid={`admin-reassign-${p.product_id}`} onClick={() => reassignProduct(p)} className="mono text-xs uppercase tracking-[0.2em] mr-3 hover:text-[#C77E3C]">Reassign</button>
                        <button onClick={() => removeProd(p.product_id)} className="mono text-xs uppercase tracking-[0.2em] hover:text-[#C77E3C]">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "sellers" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sellers.map((s) => (
                <div key={s.user_id} className="brut-border bg-white p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 brut-border bg-[#EAEAEA] overflow-hidden">{s.picture && <img src={s.picture} alt="" className="w-full h-full object-cover" />}</div>
                    <div className="flex-1">
                      <div className="heading text-xl font-bold">{s.shop_name || s.name}</div>
                      <div className="mono text-xs text-[#555] uppercase tracking-[0.2em]">{s.email}</div>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 mono text-sm">
                    <div><div className="text-[10px] uppercase tracking-[0.2em] text-[#555]">Gross</div><div className="font-bold">${s.sales.gross.toFixed(2)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-[0.2em] text-[#555]">Commission</div><div className="font-bold text-[#C77E3C]">${s.sales.commission.toFixed(2)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-[0.2em] text-[#555]">Net owed</div><div className="font-bold">${s.sales.net.toFixed(2)}</div></div>
                  </div>
                  {s.stripe_connect?.account_id && (
                    <div className="mt-3 pt-3 border-t-2 border-dashed border-[#EAEAEA]">
                      <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555]">Stripe Connect</div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${s.stripe_connect.payouts_enabled && s.stripe_connect.charges_enabled ? "bg-[#1F3D2A] text-white" : "bg-[#C77E3C] text-white"}`}>
                          {s.stripe_connect.payouts_enabled && s.stripe_connect.charges_enabled ? "Active" : "Pending"}
                        </span>
                        <span className="text-xs text-[#555] font-mono">{s.stripe_connect.account_id}</span>
                      </div>
                    </div>
                  )}
                  {s.payout && (
                    <div className="mt-3 pt-3 border-t-2 border-dashed border-[#EAEAEA]">
                      <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555]">Manual payout via {s.payout.method}</div>
                      <div className="text-sm mt-1">{s.payout.paypal_email || s.payout.remit_phone || s.payout.bank_details || "—"}</div>
                    </div>
                  )}
                  {s.address && (
                    <div className="mt-3 pt-3 border-t-2 border-dashed border-[#EAEAEA]">
                      <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555]">Address</div>
                      <div className="text-sm mt-1">{[s.address.line1, s.address.city, s.address.country].filter(Boolean).join(", ")}</div>
                    </div>
                  )}
                  {s.id_document_key && (
                    <div className="mt-2">
                      <a data-testid={`view-id-${s.user_id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/id-document/${s.id_document_key}`} target="_blank" rel="noreferrer" className="mono text-xs uppercase tracking-[0.2em] text-[#C77E3C] hover:underline">
                        View ID document →
                      </a>
                    </div>
                  )}
                  {s.blocked && (
                    <div data-testid={`blocked-badge-${s.user_id}`} className="mt-3 brut-border bg-[#C77E3C] text-white px-3 py-2 mono text-[10px] uppercase tracking-[0.25em]">
                      Blocked — products hidden from public catalogue
                    </div>
                  )}
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <button data-testid={`toggle-block-${s.user_id}`} onClick={() => toggleBlock(s)}
                      className={`brut-border px-3 py-2 mono text-[10px] uppercase tracking-[0.2em] ${s.blocked ? "bg-[#1F3D2A] text-white" : "bg-white hover:bg-[#C77E3C] hover:text-white"}`}>
                      {s.blocked ? "Unblock seller" : "Block seller"}
                    </button>
                  </div>
                  {editingSeller === s.user_id ? (
                    <div className="mt-3 pt-3 border-t-2 border-[#1F3D2A] space-y-3 bg-[#FDF3E9] -mx-5 -mb-5 px-5 pb-5 pt-4">
                      <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Edit artisan</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">Name</label>
                          <input data-testid={`edit-name-${s.user_id}`} value={sellerDraft.name} onChange={(e) => setSellerDraft({ ...sellerDraft, name: e.target.value })} className="w-full mt-1 brut-border bg-white px-3 py-2 text-sm" />
                        </div>
                        <div>
                          <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">Shop name</label>
                          <input data-testid={`edit-shop-${s.user_id}`} value={sellerDraft.shop_name} onChange={(e) => setSellerDraft({ ...sellerDraft, shop_name: e.target.value })} className="w-full mt-1 brut-border bg-white px-3 py-2 text-sm" />
                        </div>
                      </div>
                      <div>
                        <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">Payout method</label>
                        <select data-testid={`edit-method-${s.user_id}`} value={sellerDraft.payout.method} onChange={(e) => setSellerDraft({ ...sellerDraft, payout: { ...sellerDraft.payout, method: e.target.value } })} className="w-full mt-1 brut-border bg-white px-3 py-2 text-sm">
                          <option value="paypal">PayPal</option>
                          <option value="remit">Remit / Mobile money</option>
                          <option value="bank">Bank transfer</option>
                        </select>
                      </div>
                      {sellerDraft.payout.method === "paypal" && (
                        <div>
                          <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">PayPal email</label>
                          <input data-testid={`edit-paypal-${s.user_id}`} type="email" value={sellerDraft.payout.paypal_email} onChange={(e) => setSellerDraft({ ...sellerDraft, payout: { ...sellerDraft.payout, paypal_email: e.target.value } })} className="w-full mt-1 brut-border bg-white px-3 py-2 text-sm" />
                        </div>
                      )}
                      {sellerDraft.payout.method === "remit" && (
                        <>
                          <div>
                            <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">Phone (mobile money)</label>
                            <input data-testid={`edit-phone-${s.user_id}`} value={sellerDraft.payout.remit_phone} onChange={(e) => setSellerDraft({ ...sellerDraft, payout: { ...sellerDraft.payout, remit_phone: e.target.value } })} className="w-full mt-1 brut-border bg-white px-3 py-2 text-sm" />
                          </div>
                          <div>
                            <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">Country</label>
                            <input data-testid={`edit-country-${s.user_id}`} value={sellerDraft.payout.remit_country} onChange={(e) => setSellerDraft({ ...sellerDraft, payout: { ...sellerDraft.payout, remit_country: e.target.value } })} className="w-full mt-1 brut-border bg-white px-3 py-2 text-sm" />
                          </div>
                        </>
                      )}
                      {sellerDraft.payout.method === "bank" && (
                        <div>
                          <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">Bank details</label>
                          <textarea data-testid={`edit-bank-${s.user_id}`} rows={2} value={sellerDraft.payout.bank_details} onChange={(e) => setSellerDraft({ ...sellerDraft, payout: { ...sellerDraft.payout, bank_details: e.target.value } })} className="w-full mt-1 brut-border bg-white px-3 py-2 text-sm" />
                        </div>
                      )}
                      <div className="flex gap-2">
                        <button data-testid={`edit-save-${s.user_id}`} onClick={saveSellerEdit} className="brut-border bg-[#C77E3C] text-white px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-[#1F3D2A]">Save</button>
                        <button onClick={cancelSellerEdit} className="brut-border bg-white px-4 py-2 mono text-xs uppercase tracking-[0.2em]">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 pt-3 border-t-2 border-dashed border-[#EAEAEA]">
                      <button data-testid={`edit-seller-${s.user_id}`} onClick={() => startSellerEdit(s)} className="mono text-xs uppercase tracking-[0.2em] hover:text-[#C77E3C]">Edit artisan & payout →</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "orders" && (
            <div className="brut-border bg-white overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1F3D2A] text-white">
                  <tr className="mono text-[10px] uppercase tracking-[0.25em]">
                    <th className="text-left p-3">Order</th><th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Buyer</th><th className="text-right p-3">Amount</th>
                    <th className="text-right p-3">Commission</th><th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.order_id} className="border-t-2 border-[#1F3D2A]">
                      <td className="p-3 text-sm mono">{o.order_id}{o.shipping_address && <div className="text-[10px] text-[#555] mt-1 normal-case">📦 {o.shipping_address.name}, {o.shipping_address.city}, {o.shipping_address.country}</div>}</td>
                      <td className="p-3 text-sm">{new Date(o.created_at).toLocaleDateString()}</td>
                      <td className="p-3 text-sm">{o.buyer_email}</td>
                      <td className="p-3 text-sm mono text-right">${o.amount.toFixed(2)}</td>
                      <td className="p-3 text-sm mono text-right text-[#C77E3C]">${(o.commission || 0).toFixed(2)}</td>
                      <td className="p-3"><span className={`pill-label ${o.payment_status === "paid" ? "bg-[#C77E3C]" : o.payment_status === "refunded" ? "bg-[#555]" : "bg-[#1F3D2A]"}`}>{o.payment_status}</span></td>
                      <td className="p-3 text-right">
                        {o.payment_status === "paid" && (
                          <button data-testid={`refund-${o.order_id}`} onClick={() => refund(o.order_id)} className="mono text-xs uppercase tracking-[0.2em] hover:text-[#C77E3C]">Refund</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {tab === "payouts" && (
            <div className="space-y-6">
              <div className="brut-border bg-white p-5">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Manual payout ledger</div>
                    <h3 className="heading text-2xl font-bold uppercase mt-1">Amounts owed to sellers</h3>
                    <p className="text-sm text-[#555] mt-1 max-w-xl">Each seller&apos;s 70% share of every paid, non-refunded order, minus what you&apos;ve already paid them. Pay them via their listed method, then click &quot;Mark paid&quot; to record it.</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      data-testid="toggle-unpaid-only"
                      onClick={() => setUnpaidOnly((v) => !v)}
                      className={`brut-border px-4 py-3 mono text-xs uppercase tracking-[0.2em] ${unpaidOnly ? "bg-[#C77E3C] text-white" : "bg-white text-[#1F3D2A]"}`}
                    >
                      {unpaidOnly ? "★ Unpaid only" : "☐ Unpaid only"}
                    </button>
                    <button
                      data-testid="mark-all-paid"
                      onClick={markAllPaid}
                      className="brut-border bg-white text-[#1F3D2A] px-4 py-3 mono text-xs uppercase tracking-[0.2em] hover:bg-[#FDF3E9]"
                    >
                      Mark all paid
                    </button>
                    <button data-testid="export-payouts-csv" onClick={exportCsv} className="brut-border bg-[#1F3D2A] text-white px-4 py-3 mono text-xs uppercase tracking-[0.2em] hover:bg-[#C77E3C]">
                      Export CSV{unpaidOnly ? " (unpaid)" : ""}
                    </button>
                  </div>
                </div>
                <div className="mt-5 overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1F3D2A] text-white">
                      <tr className="mono text-[10px] uppercase tracking-[0.25em]">
                        <th className="text-left p-3">Seller</th>
                        <th className="text-left p-3">Method</th>
                        <th className="text-left p-3">Destination</th>
                        <th className="text-right p-3">Earned net</th>
                        <th className="text-right p-3">Paid</th>
                        <th className="text-right p-3">Owed</th>
                        <th className="text-right p-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filtered = unpaidOnly ? balances.filter((b) => b.owed > 0.005) : balances;
                        if (filtered.length === 0) {
                          return (
                            <tr data-testid="payouts-empty">
                              <td colSpan="7" className="p-8 text-center mono text-xs uppercase tracking-[0.2em] text-[#555]">
                                {unpaidOnly ? "All sellers are settled — nothing owed" : "No sellers yet"}
                              </td>
                            </tr>
                          );
                        }
                        return filtered.map((r) => {
                        const p = r.payout || {};
                        const dest = p.paypal_email || p.remit_phone || p.bank_details || "—";
                        const totalPaid = r.auto_paid + r.manual_paid;
                        return (
                          <tr key={r.seller_id} data-testid={`payout-row-${r.seller_id}`} className="border-t-2 border-[#1F3D2A]">
                            <td className="p-3 text-sm">
                              <div className="font-bold">{r.seller_name}</div>
                              <div className="mono text-[10px] text-[#555]">{r.email}</div>
                            </td>
                            <td className="p-3 text-sm mono uppercase">{p.method || "—"}{r.stripe_connect_active && <div className="text-[9px] text-[#1F3D2A]">+stripe auto</div>}</td>
                            <td className="p-3 text-xs break-all max-w-[220px]">{dest}</td>
                            <td className="p-3 text-sm mono text-right">${r.earned_net.toFixed(2)}</td>
                            <td className="p-3 text-sm mono text-right text-[#555]">${totalPaid.toFixed(2)}</td>
                            <td className="p-3 text-sm mono text-right font-bold" style={{ color: r.owed > 0 ? "#C77E3C" : "#1F3D2A" }}>${r.owed.toFixed(2)}</td>
                            <td className="p-3 text-right">
                              {r.owed > 0.005 ? (
                                <button data-testid={`mark-paid-${r.seller_id}`} onClick={() => markPaid(r)} className="brut-border bg-[#C77E3C] text-white px-3 py-2 mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#1F3D2A]">Mark paid</button>
                              ) : (
                                <span className="mono text-[10px] uppercase tracking-[0.2em] text-[#555]">settled</span>
                              )}
                            </td>
                          </tr>
                        );
                      });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="brut-border bg-white p-5">
                <h3 className="heading text-2xl font-bold uppercase">Payout history</h3>
                <p className="text-sm text-[#555] mt-1">Every manual payout you&apos;ve recorded, most recent first.</p>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1F3D2A] text-white">
                      <tr className="mono text-[10px] uppercase tracking-[0.25em]">
                        <th className="text-left p-3">Date</th>
                        <th className="text-left p-3">Seller</th>
                        <th className="text-left p-3">Method</th>
                        <th className="text-right p-3">Amount</th>
                        <th className="text-left p-3">Reference</th>
                        <th className="text-left p-3">Note</th>
                        <th className="text-left p-3">By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payoutHistory.length === 0 ? (
                        <tr><td colSpan="7" className="p-8 text-center mono text-xs uppercase tracking-[0.2em] text-[#555]">No payouts recorded yet</td></tr>
                      ) : payoutHistory.map((p) => (
                        <tr key={p.payout_id} className="border-t-2 border-[#1F3D2A]">
                          <td className="p-3 text-xs">{new Date(p.marked_at).toLocaleString()}</td>
                          <td className="p-3 text-sm"><div className="font-bold">{p.seller_name}</div><div className="mono text-[10px] text-[#555]">{p.seller_email}</div></td>
                          <td className="p-3 text-sm mono uppercase">{p.method || "—"}</td>
                          <td className="p-3 text-sm mono text-right font-bold">${p.amount.toFixed(2)}</td>
                          <td className="p-3 text-xs mono break-all max-w-[160px]">{p.reference || "—"}</td>
                          <td className="p-3 text-xs max-w-[200px]">{p.note || "—"}</td>
                          <td className="p-3 text-xs mono">{p.marked_by_email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === "tickets" && (
            <div className="brut-border bg-white overflow-x-auto">
              <div className="p-5 border-b-2 border-[#1F3D2A]">
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ All tickets issued</div>
                <h3 className="heading text-2xl font-bold uppercase mt-1">Sold tickets ({tickets.length})</h3>
                <p className="text-sm text-[#555] mt-1">Each ticket sold has a unique number and buyer name. Click a ticket to open the printable version.</p>
              </div>
              <table className="w-full">
                <thead className="bg-[#1F3D2A] text-white">
                  <tr className="mono text-[10px] uppercase tracking-[0.25em]">
                    <th className="text-left p-3">Issued</th>
                    <th className="text-left p-3">Ticket #</th>
                    <th className="text-left p-3">Event</th>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Holder</th>
                    <th className="text-left p-3">Buyer</th>
                    <th className="text-left p-3">Order</th>
                    <th className="text-right p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.length === 0 ? (
                    <tr><td colSpan="8" className="p-8 text-center mono text-xs uppercase tracking-[0.2em] text-[#555]">No tickets issued yet</td></tr>
                  ) : tickets.map((t) => (
                    <tr key={t.ticket_id} data-testid={`admin-ticket-${t.ticket_id}`} className="border-t-2 border-[#1F3D2A]">
                      <td className="p-3 text-xs">{new Date(t.issued_at).toLocaleString()}</td>
                      <td className="p-3 text-xs mono font-bold">{t.ticket_number}</td>
                      <td className="p-3 text-sm font-bold">{t.event_name}</td>
                      <td className="p-3 text-xs">{t.event_date || "—"}</td>
                      <td className="p-3 text-sm">{t.buyer_full_name}</td>
                      <td className="p-3 text-xs">{t.buyer_email}</td>
                      <td className="p-3 text-xs mono">{t.order_id}</td>
                      <td className="p-3 text-right"><button onClick={() => openAdminTicket(t.ticket_id)} className="mono text-xs uppercase tracking-[0.2em] hover:text-[#C77E3C]">Open</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "reminders" && (
            <div className="brut-border bg-white overflow-x-auto">
              <div className="p-5 border-b-2 border-[#1F3D2A] flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Automation log</div>
                  <h3 className="heading text-2xl font-bold uppercase mt-1">Reminder log ({reminderLog.length})</h3>
                  <p className="text-sm text-[#555] mt-1 max-w-xl">Every automated email dispatched by Jubasmash — delivery reminders 2 days before ETA, event reminders 2 days before the show, and the payout-due digest on the last Monday of every month.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button data-testid="run-reminders-btn" onClick={runReminders} className="brut-border bg-white px-4 py-3 mono text-xs uppercase tracking-[0.2em] hover:bg-[#1F3D2A] hover:text-white">Run reminders now</button>
                  <button data-testid="test-payout-email-btn" onClick={testPayoutEmail} className="brut-border bg-[#C77E3C] text-white px-4 py-3 mono text-xs uppercase tracking-[0.2em] hover:bg-[#1F3D2A]">Preview payout-due email</button>
                </div>
              </div>
              <table className="w-full">
                <thead className="bg-[#1F3D2A] text-white">
                  <tr className="mono text-[10px] uppercase tracking-[0.25em]">
                    <th className="text-left p-3">Sent</th>
                    <th className="text-left p-3">Kind</th>
                    <th className="text-left p-3">Recipient</th>
                    <th className="text-left p-3">Target</th>
                    <th className="text-left p-3">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {reminderLog.length === 0 ? (
                    <tr><td colSpan="5" className="p-8 text-center mono text-xs uppercase tracking-[0.2em] text-[#555]">No reminders sent yet</td></tr>
                  ) : reminderLog.map((r) => (
                    <tr key={r.log_id} data-testid={`reminder-row-${r.log_id}`} className="border-t-2 border-[#1F3D2A]">
                      <td className="p-3 text-xs">{new Date(r.sent_at).toLocaleString()}</td>
                      <td className="p-3"><span className={`pill-label ${r.kind === "payout_due" ? "bg-[#C77E3C]" : r.kind === "event" ? "bg-[#1F3D2A]" : "bg-[#555]"}`}>{r.kind}</span></td>
                      <td className="p-3 text-xs">{r.target_email}</td>
                      <td className="p-3 text-xs mono">{r.target_id}</td>
                      <td className="p-3 text-xs">
                        {r.kind === "delivery" && `ETA ${r.eta || "—"}`}
                        {r.kind === "event" && `${r.event_name || ""} · ${r.event_date || ""}`}
                        {r.kind === "payout_due" && `$${(r.total_owed || 0).toFixed(2)} to ${r.seller_count || 0} seller(s)${r.manual ? " · manual" : ""}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "featured" && (
            <div className="space-y-6">
              <div className="brut-border bg-white p-5">
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Homepage hero</div>
                <h3 className="heading text-2xl font-bold uppercase mt-1">{editingFeat ? "Edit featured artisan" : "Add featured artisan"}</h3>
                <p className="text-sm text-[#555] mt-1">The first active entry (lowest sort order) is shown on the homepage hero. Deactivate to hide without deleting.</p>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Name *</label>
                    <input data-testid="feat-name" value={featForm.name} onChange={(e) => setFeatForm({ ...featForm, name: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" placeholder="Anya Maker" />
                  </div>
                  <div>
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Subtitle</label>
                    <input data-testid="feat-subtitle" value={featForm.subtitle} onChange={(e) => setFeatForm({ ...featForm, subtitle: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" placeholder="Clay & Vinyl Studio · Lisbon" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Hero image *</label>
                    <div className="mt-2 flex items-start gap-4">
                      <div className="w-32 h-40 brut-border bg-[#EAEAEA] flex-shrink-0 overflow-hidden">
                        {featForm.hero_image && <img src={featForm.hero_image} alt="preview" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 space-y-2">
                        <input data-testid="feat-image-url" value={featForm.hero_image} onChange={(e) => setFeatForm({ ...featForm, hero_image: e.target.value })} className="w-full brut-border bg-white px-4 py-3" placeholder="Paste image URL or upload below" />
                        <label className="brut-border bg-white px-4 py-3 mono text-xs uppercase tracking-[0.2em] inline-block cursor-pointer hover:bg-[#1F3D2A] hover:text-white">
                          {featUploading ? "Uploading…" : "Upload image"}
                          <input data-testid="feat-image-file" type="file" accept="image/*" className="hidden" onChange={(e) => uploadFeaturedImage(e.target.files?.[0])} />
                        </label>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Sort order</label>
                    <input data-testid="feat-sort" type="number" value={featForm.sort_order} onChange={(e) => setFeatForm({ ...featForm, sort_order: parseInt(e.target.value) || 0 })} className="w-full mt-2 brut-border bg-white px-4 py-3" />
                  </div>
                  <div>
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Active</label>
                    <label className="mt-2 flex items-center gap-2 brut-border bg-white px-4 py-3 cursor-pointer">
                      <input data-testid="feat-active" type="checkbox" checked={featForm.active} onChange={(e) => setFeatForm({ ...featForm, active: e.target.checked })} />
                      <span className="mono text-xs uppercase tracking-[0.2em]">{featForm.active ? "Shown on site" : "Hidden"}</span>
                    </label>
                  </div>
                </div>
                <div className="mt-5 flex gap-3">
                  <button data-testid="feat-save" onClick={saveFeatured} className="btn-primary">{editingFeat ? "Update" : "Add featured artisan"}</button>
                  {editingFeat && (
                    <button data-testid="feat-cancel" onClick={() => { setEditingFeat(null); setFeatForm({ name: "", subtitle: "", hero_image: "", active: true, sort_order: 0 }); }} className="btn-ghost">Cancel</button>
                  )}
                </div>
              </div>

              <div className="brut-border bg-white overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#1F3D2A] text-white">
                    <tr className="mono text-[10px] uppercase tracking-[0.25em]">
                      <th className="text-left p-3">Image</th>
                      <th className="text-left p-3">Name</th>
                      <th className="text-left p-3">Subtitle</th>
                      <th className="text-right p-3">Order</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {featured.length === 0 ? (
                      <tr><td colSpan="6" className="p-8 text-center mono text-xs uppercase tracking-[0.2em] text-[#555]">No featured artisans yet</td></tr>
                    ) : featured.map((f) => (
                      <tr key={f.featured_id} data-testid={`feat-row-${f.featured_id}`} className="border-t-2 border-[#1F3D2A]">
                        <td className="p-3"><div className="w-14 h-14 brut-border bg-[#EAEAEA] overflow-hidden">{f.hero_image && <img src={f.hero_image} alt={f.name} className="w-full h-full object-cover" />}</div></td>
                        <td className="p-3 text-sm font-bold">{f.name}</td>
                        <td className="p-3 text-xs text-[#555]">{f.subtitle || "—"}</td>
                        <td className="p-3 text-sm mono text-right">{f.sort_order}</td>
                        <td className="p-3"><span className={`pill-label ${f.active ? "bg-[#C77E3C]" : "bg-[#555]"}`}>{f.active ? "active" : "hidden"}</span></td>
                        <td className="p-3 text-right whitespace-nowrap">
                          <button data-testid={`feat-edit-${f.featured_id}`} onClick={() => editFeatured(f)} className="mono text-xs uppercase tracking-[0.2em] mr-3 hover:text-[#C77E3C]">Edit</button>
                          <button data-testid={`feat-delete-${f.featured_id}`} onClick={() => deleteFeatured(f.featured_id)} className="mono text-xs uppercase tracking-[0.2em] hover:text-[#C77E3C]">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "theme" && (
            <div className="brut-border bg-white p-6 max-w-2xl">
              <h3 className="heading text-2xl font-bold uppercase">Site theme</h3>
              <p className="text-sm text-[#555] mt-1">Change Jubasmash&apos;s colors. Preview is live as you pick; click Save to persist.</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  ["bg", "Background"], ["ink", "Ink / borders / dark text"],
                  ["accent", "Primary accent"], ["accent_deep", "Accent hover"],
                ].map(([k, lbl]) => (
                  <div key={k}>
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">{lbl}</label>
                    <div className="mt-2 flex items-center gap-3 brut-border bg-white px-3 py-2">
                      <input data-testid={`theme-${k}`} type="color" value={theme[k]}
                        onChange={(e) => previewTheme({ ...theme, [k]: e.target.value })}
                        className="w-12 h-10 cursor-pointer border-0 p-0 bg-transparent" />
                      <input type="text" value={theme[k]}
                        onChange={(e) => previewTheme({ ...theme, [k]: e.target.value })}
                        className="flex-1 mono text-sm px-2 py-1 bg-transparent focus:outline-none" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex gap-3">
                <button data-testid="save-theme-btn" onClick={saveTheme} className="btn-primary">Save theme</button>
                <button data-testid="reset-theme-btn" onClick={resetTheme} className="btn-ghost">Reset to default</button>
              </div>
            </div>
          )}
        </div>
      </section>
      <RestockHistoryDrawer product={historyProduct} onClose={() => setHistoryProduct(null)} />
      {editingProductId && (
        <AdminProductEdit
          productId={editingProductId}
          onClose={() => setEditingProductId(null)}
          onSaved={() => { setEditingProductId(null); refresh(); }}
        />
      )}
    </Layout>
  );
}
